/* StatisticsChart.js */
import React, { useEffect, useMemo, useRef } from "react";
import ApexCharts from "apexcharts";
import { useSelector } from "react-redux";
import { useGetStatisticChartData } from "../../../../swrHelpers/dashboard";
import Select from "react-select";
import "./StatisticChart.scss";

// Static data
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default function StatisticChart({ dashboardSelectedCurrency }) {
  const chartRef = useRef(null);
  const { generalSettings } = useSelector((state) => state.settings);
  const currency = generalSettings.currencyId?.symbol;
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [chartData, setChartData] = React.useState({
    investments: [],
    repayments: [],
    revenue: [],
    secondaryMarketRevenue: [],
  });

  const { data } = useGetStatisticChartData(dashboardSelectedCurrency, year);

  useEffect(() => {
    if (data) {
      setChartData({
        investments: data.investmentData?.map((item) => item.amount) || [],
        repayments: data.repaymentData?.map((item) => item.amount) || [],
        revenue: data.revenueData?.map((item) => item.amount) || [],
        secondaryMarketRevenue:
          data.secondaryMarketData?.map((item) => item.amount) || [],
      });
    }
  }, [data]);

  useEffect(() => {
    const chart = new ApexCharts(chartRef.current, getOptions(chartData));
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [chartData]);

  const yearOptions = useMemo(() => {
    return Array.from({ length: 5 }).map((_, index) => {
      const y = new Date().getFullYear() - index;
      return { value: y, label: y.toString() };
    });
  }, []);

  const handleYearChange = (selectedOption) => {
    setYear(selectedOption?.value);
  };

  // ApexChart Options
  function getOptions({
    investments,
    repayments,
    revenue,
    secondaryMarketRevenue,
  }) {
    return {
      chart: {
        type: "area",
        height: 400,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      },
      dataLabels: { enabled: false },
      series: [
        { name: "Investments", data: investments },
        { name: "Repayments", data: repayments },
        { name: "Revenue", data: revenue },
        { name: "Secondary Market", data: secondaryMarketRevenue },
      ],
      colors: ["#4AD191", "#FF9066", "#CE5EEF", "#FF55BF"],
      stroke: { curve: "smooth", width: 2 },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 0,
          opacityFrom: 0.6,
          opacityTo: 0.1,
          stops: [0, 60],
        },
      },
      grid: {
        borderColor: "#e9ecef",
        strokeDashArray: 2,
      },
      xaxis: {
        categories: MONTHS,
        labels: {
          style: { colors: "#868e96", fontSize: "12px" },
        },
        axisBorder: { show: true, color: "#dee2e6" },
      },
      yaxis: {
        min: 0,
        max:
          data?.minMaxAmount?.maxAmount > 1000
            ? Math.ceil((data?.minMaxAmount?.maxAmount || 0) / 100) * 100 + 1000
            : 1400,
        tickAmount: 7,
        labels: {
          offsetX: -10,
          style: { colors: "#868e96", fontSize: "12px" },
          formatter: function (val) {
            return `${currency}${Math.round(val)}`;
          },
        },
      },
      legend: {
        show: true,
        position: "top",
        horizontalAlign: "center",
        floating: false,
        markers: {
          width: 10,
          height: 10,
          radius: 10,
        },
        itemMargin: {
          horizontal: 16,
          vertical: 4,
        },
        fontSize: "14px",
        fontWeight: 600,
        fontFamily: "montserrat,  sans-serif",
        labels: {
          colors: "#000",
        },
      },
      tooltip: {
        theme: "light",
        x: { format: "MMM" },
        y: {
          formatter: function (val) {
            return `${currency}${val.toLocaleString(undefined, {
              maximumFractionDigits: 0,
            })}`;
          },
        },
      },
    };
  }

  return (
    <div className="statistic-chart-wrapper py-5 px-4">
      <header className="statistic-chart-header">
        <h2 className="statistic-chart-title mb-0">Statistics</h2>
        <div className="d-flex gap-10">
          <div className="year-selector flex-shrink-0">
            <Select
              className="filter-select-container"
              classNamePrefix="year-select"
              options={yearOptions}
              value={yearOptions.find((opt) => opt.value === year)}
              onChange={handleYearChange}
              isSearchable={false}
            />
          </div>
        </div>
      </header>
      <div ref={chartRef} />
    </div>
  );
}
